import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import type { AllPermissions } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-sidebar-item.js';
import '@chancenwerk/design/components/fonts.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

type DynamicPermissionFunc = (user: Maybe<ChancenwerkUser>) => boolean;

export interface SidebarLinkDefinition {
  label: string;
  permission?: AllPermissions | DynamicPermissionFunc | Array<AllPermissions | DynamicPermissionFunc>;
  route: string;
  icon?: string;
  showWithoutPermission?: boolean;
  hidden?: boolean;
}

export interface SidebarSubsectioDefinition {
  name: string;
  links: SidebarLinkDefinition[];
}

export interface SidebarSectionDefinition {
  name: string;
  subsections: SidebarSubsectioDefinition[];
}

/**
 * Creates a DynamicPermissionFunc to check if the user has ALL the permissions
 * @param permissions All the permissions that the user needs to have
 * @returns DynamicPermissionFunc
 */
export function HasAllPermissions(...permissions: AllPermissions[]): DynamicPermissionFunc {
  return (user: Maybe<ChancenwerkUser>) => {
    if (!user) return false;
    return !permissions.some(p => !user.hasPermission(p, ANY_CONTEXT));
  };
}

/**
 * Creates a DynamicPermissionFunc to check if the user has at least ONE of the permissions
 * @param permissions The permissions that the user needs to have one from
 * @returns DynamicPermissionFunc
 */
export function HasOnePermission(...permissions: AllPermissions[]): DynamicPermissionFunc {
  return (user: Maybe<ChancenwerkUser>) => {
    if (!user) return false;
    return permissions.some(p => user.hasPermission(p, ANY_CONTEXT));
  };
}

/**
 * @customElement chancen-component-auto-navigation
 */
@customElement('chancen-component-auto-navigation')
export class ChancenComponentAutoNavigation extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          height: '100%',
        },
        '.chevron': {
          cursor: 'pointer',
          fontSize: '1.8rem',
          transition: 'all 0.1s ease-in-out',
        },
        '.chevron:hover': {
          transform: 'scale(1.5, 1.5)',
        },
        '.chevron-left:hover': {
          transform: 'translateX(-2px)',
        },
        '.chevron-right:hover': {
          transform: 'translateX(2px)',
        },
        '.chevron-hide': {
          visibility: 'hidden',
        },
      },
    ];
  }

  @property({ attribute: false }) sections: SidebarSectionDefinition[] = [];
  @property({ attribute: false, reflect: true }) active: string = '';
  @property() mode: 'Chevron' | 'All' = 'Chevron';

  @state() readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _lastActiveSectionIndex = new LocalStorageBehaviorController(
    this,
    'config:last-sidebar-section',
    -1,
  );

  public override updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (this.sections.length > 0 && (!this._activeSection() || this._activeSectionNoPermission())) {
      this.active =
        this._visibleSections().at(this._lastActiveSectionIndex.value ?? this.sections.length - 1)?.name ??
        this._firstAllowedSection()?.name ??
        '';
    }
    super.updated(_changedProperties);
  }

  override render() {
    switch (this.mode) {
      case 'Chevron':
        return html`<cr-flex space-between ${css({ height: '100%', overflowX: 'hidden' })}>
          ${this._sectionTemplate(this._activeSection())} ${this._chevron()}
        </cr-flex> `;
      case 'All':
        return html`<cr-flex ${css({ height: '100%', overflowX: 'hidden' })}
          >${this._visibleSections().map(s => this._sectionTemplate(s))}</cr-flex
        >`;
    }
    return nothing;
  }

  private _chevron() {
    const sections = this._visibleSections();
    if (sections.length <= 1) {
      return nothing;
    }

    const index = this._activeSectionIndex();
    if (index === null || index === undefined) {
      return nothing;
    }

    return html`<cr-flex
      padding="md"
      ${css({
        color: this.colors.white,
        userSelect: 'none',
        marginTop: this.spacing.md,
      })}
      horizontal
      center
      crossaxis-center
      space-between
    >
      ${index !== 0
        ? html`
            <cr-icon
              class="chevron chevron-left"
              @click=${() => {
                this.active = sections[index - 1]?.name ?? '';
                this._lastActiveSectionIndex.next(index - 1);
                this._emitOnSectionChange();
              }}
            >
              chevron_left
            </cr-icon>
          `
        : html` <cr-icon class="chevron chevron-left chevron-hide"> chevron_left </cr-icon> `}
      <cr-flex space="md" crossaxis-center>
        <cr-text ${css({ fontSize: '0.7rem' })}>Aktuelle Ansicht</cr-text>
        <cr-text
          bold
          ${css({
            backgroundColor: 'rgba(0,0,0,0.05)',
            padding: this.spacing.sm,
            borderRadius: this.sizes.borderRadiusSecondary,
          })}
        >
          ${this.active}
        </cr-text>
      </cr-flex>
      ${index !== sections.length - 1
        ? html`
            <cr-icon
              class="chevron chevron-right"
              @click=${() => {
                this.active = sections[index + 1]?.name ?? '';
                this._lastActiveSectionIndex.next(index + 1);
                this._emitOnSectionChange();
              }}
            >
              chevron_right
            </cr-icon>
          `
        : html` <cr-icon class="chevron chevron-right chevron-hide"> chevron_right </cr-icon> `}
    </cr-flex>`;
  }

  private _emitOnSectionChange() {
    const section = this._visibleSections().find(s => s.name === this.active);
    this.dispatchEvent(
      new CustomEvent('onSectionChange', {
        detail: {
          name: section?.name ?? '',
          subsections:
            section?.subsections.filter(s =>
              s.links.some(
                l =>
                  !l.permission ||
                  l.showWithoutPermission ||
                  (Array.isArray(l.permission)
                    ? l.permission.some(p => this._checkPermission(p))
                    : this._checkPermission(l.permission)),
              ),
            ) ?? [],
        },
      }),
    );
  }

  private _checkPermission(permission: AllPermissions | DynamicPermissionFunc) {
    if (typeof permission === 'function') {
      return permission(this._currentUser.value);
    }
    return this._currentUser.value?.hasPermission(permission, ANY_CONTEXT);
  }

  private _visibleSections() {
    return this.sections.filter(section =>
      section.subsections.some(s =>
        s.links.some(
          l =>
            !l.permission ||
            l.showWithoutPermission ||
            (Array.isArray(l.permission)
              ? l.permission.some(p => this._checkPermission(p))
              : this._checkPermission(l.permission)),
        ),
      ),
    );
  }

  private _sectionTemplate(section: Maybe<SidebarSectionDefinition>) {
    if (!section) {
      return nothing;
    }

    const visible = section.subsections.filter(s =>
      s.links.some(
        l =>
          !l.permission ||
          l.showWithoutPermission ||
          (Array.isArray(l.permission)
            ? l.permission.some(p => this._checkPermission(p))
            : this._checkPermission(l.permission)),
      ),
    );

    if (visible.length === 0) {
      return nothing;
    }

    return html`
      ${section.name && this.mode === 'All'
        ? html`
            <cr-flex padding="md">
              <cr-h4 ${css({ color: 'white', marginBottom: '0' })}> ${section.name} </cr-h4>
            </cr-flex>
          `
        : nothing}
      <cr-flex>
        ${repeat(
          visible,
          s => s.name,
          s => this._subSectionTemplate(s),
        )}
      </cr-flex>
    `;
  }

  private _subSectionTemplate(section: Maybe<SidebarSubsectioDefinition>) {
    if (!section) {
      return nothing;
    }

    const visible = section.links.filter(
      l =>
        !l.permission ||
        l.showWithoutPermission ||
        (Array.isArray(l.permission)
          ? l.permission.some(p => this._checkPermission(p))
          : this._checkPermission(l.permission)),
    );

    if (visible.length === 0) {
      return nothing;
    }

    return html`
      ${section.name
        ? html`<cr-flex padding="md">
            <cr-text bold ${css({ color: 'white', marginBottom: '0' })}>${section.name}</cr-text>
          </cr-flex>`
        : nothing}
      <cr-flex>
        ${repeat(
          visible,
          l => l.label,
          l =>
            l.showWithoutPermission &&
            l.permission &&
            !(Array.isArray(l.permission)
              ? l.permission.some(p => this._checkPermission(p))
              : this._checkPermission(l.permission))
              ? html`
                  <cr-sidebar-item
                    ${css({ display: l.hidden ? 'none' : undefined })}
                    .text=${l.label}
                    .icon=${l.icon ?? ''}
                    deactivated
                  ></cr-sidebar-item>
                `
              : html`
                  <cr-sidebar-item
                    ${css({ display: l.hidden ? 'none' : undefined })}
                    .text=${l.label}
                    .icon=${l.icon ?? ''}
                    href=${l.route}
                  ></cr-sidebar-item>
                `,
        )}
      </cr-flex>
    `;
  }

  private _activeSection(): Maybe<SidebarSectionDefinition> {
    return this._visibleSections().find(s => s.name === this.active);
  }

  private _activeSectionNoPermission() {
    return !this._activeSection()?.subsections.some(s =>
      s.links.some(
        l =>
          !l.permission ||
          (Array.isArray(l.permission)
            ? l.permission.some(p => this._checkPermission(p))
            : this._checkPermission(l.permission)),
      ),
    );
  }

  private _activeSectionIndex(): Maybe<number> {
    return this._visibleSections().findIndex(s => s.name === this.active);
  }

  private _firstAllowedSection(): Maybe<SidebarSectionDefinition> {
    const sections = this._visibleSections();
    if (sections.length === 0) {
      return null;
    }

    return sections[sections.length - 1];
  }
}
